import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { UserCard } from 'types/interfaces/user/UserCard';

import { getRandomItem } from 'helpers/array';
import { useMyLikesPopupQuery } from 'hooks/queries/useMyLikesPopupQuery';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Spinner, SpinnerTypes } from 'components/base/Spinner';
import { Popup } from 'components/UI/Popup';

import { ReactComponent as LikeIcon } from './images/like.svg';
import { ReactComponent as ProfileMessageIcon } from './images/message.svg';

import css from './myLikesPopup.module.sass';

type ActionsPayload =
  | BasePopupActionPayload<'mount'>
  | (BasePopupActionPayload<'submit'> & { user: UserCard })
  | BasePopupActionPayload<'close'>;

type Props = BasePopupProps<null, (action: ActionsPayload) => void>;

export const MyLikesPopup: React.FC<Props> = ({ onAction }) => {
  const { t } = useTranslation('app');

  const myLikesPopupQuery = useMyLikesPopupQuery();

  const users = myLikesPopupQuery.data?.likes || [];
  const randomUser = getRandomItem(users);

  useEffect(() => {
    onAction({ type: 'mount' });
  }, [onAction]);

  return (
    <Popup onClose={() => onAction({ type: 'close' })}>
      <p className={css.title}>{t('retentionFunnels.myLikesPopup.title')}</p>
      <p className={css.description}>
        {t('retentionFunnels.myLikesPopup.description')}
      </p>
      {myLikesPopupQuery.isLoading && (
        <Spinner type={SpinnerTypes.Dark} className={css.spinner} />
      )}

      {myLikesPopupQuery.isSuccess && (
        <ul className={css.usersList}>
          {myLikesPopupQuery.data.likes.map((user) => {
            return (
              <li key={user.id} className={css.usersItem}>
                <div className={css.user}>
                  <div className={css.photoWrapper}>
                    <img
                      className={css.photo}
                      src={user.photo_url || undefined}
                      alt="user"
                    />
                  </div>
                  <ul className={css.actionsList}>
                    <li className={css.actionsItem}>
                      <ProfileMessageIcon className={css.actionIcon} />
                    </li>
                    <li className={css.actionsItem}>
                      <LikeIcon
                        className={cx(css.actionIcon, css.actionIconFillAccent)}
                      />
                    </li>
                  </ul>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <BaseButton
        type={ButtonTypes.Accent}
        className={css.submit}
        onClick={() => onAction({ type: 'submit', user: randomUser })}
      >
        {t('retentionFunnels.myLikesPopup.submit')}
      </BaseButton>
    </Popup>
  );
};
